import './index.less';
import React, { useEffect, useRef, useState } from 'react';
import { BaseOperateView, BaseStagePlugin, useFramework, useIsLongImageMode } from '@/framework';
import { IExportPlugin, PluginType } from '@/version-types';

const StageHover: React.FC = React.memo(() => {
    const framework = useFramework();
    const isLongImageMode = useIsLongImageMode();
    const { editor, frame, transform, edgelessCanvas, events } = framework.service;
    const originIndexRef = useRef<number | null>(null);

    const [_, refresh] = useState(0);

    useEffect(() => {
        if (!isLongImageMode) return;
        let downOriginIndex: number | null = null;
        const applyHoverOriginIndex = (originIndex: number | null, force?: boolean) => {
            const isBaseOperate = editor.isBaseOperate;
            const isDisablePick = editor.disablePick;
            const isMouseSelect = editor.isMouseSelect;
            if (originIndex === null) {
                if (isMouseSelect) {
                    originIndexRef.current = null;
                } else if (!isBaseOperate && !isDisablePick) {
                    originIndexRef.current = null;
                }
            } else if (isMouseSelect) {
                originIndexRef.current = downOriginIndex;
            } else if (!isBaseOperate && (!isDisablePick || force)) {
                originIndexRef.current = originIndex;
            }
            refresh((v) => v + 1);
        };
        const mouseDownHandler = editor.onMouseDown((e) => {
            const stageIndex = transform.getFrameIndexByMousePointY(e.clientY);
            downOriginIndex = frame.indexToFrameId(stageIndex);
            if (editor.isMouseSelect) {
                applyHoverOriginIndex(downOriginIndex);
            }
        });
        const mouseUpHandler = editor.onMouseUp(() => {
            downOriginIndex = null;
        });
        const mouseEnterHandler = editor.onMouseEnter((originIndex, force) => {
            applyHoverOriginIndex(originIndex, force);
        });
        const mouseLeaveHandler = editor.onMouseLeave(() => {
            applyHoverOriginIndex(null);
        });
        const mouseMoveHandler = editor.onMouseMove((ev) => {
            const isBaseOperate = editor.isBaseOperate;
            const isDisablePick = editor.disablePick;
            const isMouseSelect = editor.isMouseSelect;
            if (isMouseSelect) {
                if (downOriginIndex !== null && originIndexRef.current !== downOriginIndex) {
                    originIndexRef.current = downOriginIndex;
                    refresh((v) => v + 1);
                }
                return;
            }
            if (isBaseOperate || isDisablePick) return;
            const mPoint = { x: ev.pageX, y: ev.pageY };
            const stageIndex = transform.getFrameIndexByMousePointY(mPoint.y);
            if (!transform.checkPointInFrame(mPoint, stageIndex)) {
                if (originIndexRef.current !== null) {
                    applyHoverOriginIndex(null);
                }
                return;
            }
            const originIndex = frame.indexToFrameId(stageIndex);
            if (originIndexRef.current !== originIndex) {
                applyHoverOriginIndex(originIndex);
            }
        });
        const resizeHandler = frame.onResize(() => {
            if (originIndexRef.current !== null) {
                refresh((v) => v + 1);
            }
        });
        const panHandler = edgelessCanvas.onPan(() => {
            if (originIndexRef.current !== null) {
                refresh((v) => v + 1);
            }
        });
        const activeInfoHandler = frame.onActiveInfoChange(() => {
            if (originIndexRef.current !== null) {
                refresh((v) => v + 1);
            }
        });
        return () => {
            events.off(mouseEnterHandler);
            events.off(mouseLeaveHandler);
            events.off(mouseDownHandler);
            events.off(mouseUpHandler);
            events.off(mouseMoveHandler);
            events.off(resizeHandler);
            events.off(panHandler);
            events.off(activeInfoHandler);
        };
    }, [edgelessCanvas, editor, events, frame, isLongImageMode, transform]);

    if (originIndexRef.current === null || !isLongImageMode || edgelessCanvas.getHiddenAll()) {
        return null;
    }

    const activeLayer = frame.getActiveLayer();
    const activeLayerFrameId = activeLayer ? frame.getLayerFrameId(activeLayer.id) : null;
    if (activeLayerFrameId === originIndexRef.current) {
        return null;
    }

    const shape = frame.getShape(originIndexRef.current);

    return (
        <BaseStagePlugin name="stage-hover">
            <BaseOperateView layer={shape}>
                <div className="c-stage-hover"></div>
            </BaseOperateView>
        </BaseStagePlugin>
    );
});

const exportPlugin: IExportPlugin = {
    type: PluginType.STAGE,
    sort: -2,
    name: 'stage-hover',
    render() {
        return <StageHover />;
    },
};

export default exportPlugin;
